/**
 * Heidi Work / Projects analytics helpers.
 *
 * Lives under models/analytics (not features/projects) so Chat-quarantined
 * surfaces can emit without growing chat→features outbound couplings.
 *
 * Consolidated behavioural events (oscerai/snowplow#154) — variation via
 * action / outcome / object_type props. Snowplow-first: amplitude/posthog off.
 * `client_platform` is auto-enriched by `lib/analytics/wiring.ts`.
 */
import type {
  WorkLaunchAnalyticsProps as WorkBaseProps,
  WorkSeatType,
  WorkUpgradeActionedProps,
} from '@heidi/agents/contracts';
import { WorkLaunchEventName, trackWorkEvent } from '@heidi/agents/data';
import { getCurrentUserBillingInfo } from '@heidi/commerce/data';
import type { BillingSeatType } from '@heidi/schema/ml-scribe';

// Likewise the launch-event payload shapes (D9 ruling 27): the event NAMES are
// declared in `@heidi/agents/contributions` and their PROP TYPES in
// `@heidi/agents/contracts`, so the pair has one home. `WorkBaseProps` keeps
// its name at this path — every importer, and every `jest.mock` literal, still
// resolves unchanged.
export type { WorkLaunchAnalyticsProps } from '@heidi/agents/contracts';
export type { WorkBaseProps, WorkUpgradeActionedProps };

// ─────────────────────────────────────────────────────────────────────────────
// Upgrade / paywall — single event, action discriminator
// Schema: iglu:com.heidi/work_upgrade_actioned/jsonschema/1-0-0
// ─────────────────────────────────────────────────────────────────────────────

export function trackWorkUpgradeActioned(props: WorkUpgradeActionedProps): void {
  trackWorkEvent(WorkLaunchEventName.WorkUpgradeActioned, props);
}

export function resolveWorkBillingAnalyticsProps(): {
  current_plan?: string;
  current_seat_type?: WorkSeatType;
} {
  if (typeof getCurrentUserBillingInfo !== 'function') return {};
  let billing: ReturnType<typeof getCurrentUserBillingInfo>;
  try {
    billing = getCurrentUserBillingInfo();
  } catch {
    return {};
  }
  if (!billing) return {};

  const plan = billing.new_product_tier ?? billing.tier;
  const seat = mapBillingSeatType(billing.seat_type);

  return {
    ...(plan != null ? { current_plan: String(plan) } : {}),
    ...(seat != null ? { current_seat_type: seat } : {}),
  };
}

function mapBillingSeatType(
  seatType: BillingSeatType | string | null | undefined
): WorkSeatType | undefined {
  if (seatType == null) return undefined;
  const normalised = String(seatType).toLowerCase();
  if (normalised === 'max') return 'max';
  if (normalised === 'standard') return 'standard';
  if (normalised === 'assistant') return 'assistant';
  return undefined;
}

/**
 * Account SKU and this user's seat from the already-loaded billing cache.
 * Omits fields when the cache is cold. Never starts a fetch.
 */
export function trackWorkArtifactCreated(props: {
  project_id?: string;
  agent_run_id?: string;
  artifact_type?: string;
  outcome?: 'succeeded' | 'failed';
}): void {
  trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
    action: 'created',
    object_type: 'artifact',
    surface: 'project_chat',
    ...resolveWorkBillingAnalyticsProps(),
    ...props,
  });
}

export function trackWorkArtifactExported(props: {
  project_id?: string;
  agent_run_id?: string;
  artifact_type?: string;
  outcome?: 'succeeded' | 'failed';
}): void {
  trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
    action: 'exported',
    object_type: 'artifact',
    surface: 'project_chat',
    ...resolveWorkBillingAnalyticsProps(),
    ...props,
  });
}
